"use client";

import Link from "next/link";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  coalesceCourseMoney,
  formatCourseMoneyEnIn,
  getLocalizedField,
  type Course,
  type CoursePrice,
} from "../api";
import type { Locale } from "@/lib/i18n";
import { useAuth } from "@/components/providers/auth-provider";
import { useLibrary } from "@/features/dashboard/hooks";
import { useCoursePricing } from "./CoursePricingProvider";
import { CourseEnrollCta } from "./CourseEnrollCta";
import { useCoursePlanPricingUiModel } from "../hooks/use-course-plan-pricing-ui-model";
import {
  formatTierUpgradeCtaLabel,
  isPlanIdActiveUpgradeTarget,
  resolvePlanPricingUiKind,
  resolvePricingUiOwnedState,
} from "../lib/resolve-course-plan-pricing-ui-state";
import { getOwnershipCopy } from "../lib/ownership-copy";
import { useCourseEligibilityStatus } from "../use-course-eligibility-status";
import { useScrolledPast } from "../lib/use-scrolled-past";
import { HERO_BUY_ID } from "./CourseHeroPurchaseRow";
import {
  CourseMobileSectionSheet,
  type CoursePageSection,
} from "./CourseSectionNavigator";
import { signInHref } from "@/lib/post-login-redirect";

function formatCompactPrice(price: CoursePrice): string {
  if (price.isFree) return "Free";
  const list = coalesceCourseMoney(price.list);
  const sale = price.sale;
  const amount =
    sale !== undefined && Number.isFinite(sale) && sale < list ? sale : list;
  const symbol = price.currency === "BDT" ? "৳" : "$";
  return `${symbol}${formatCourseMoneyEnIn(amount)}`;
}

type Props = {
  courseId: string;
  slug: string;
  fallbackPrice: CoursePrice;
  eligibility?: Course["eligibility"];
  locale: Locale;
  firstLessonId?: string | null;
  sections: CoursePageSection[];
};

export function CourseMobilePricingBar({
  courseId,
  slug,
  fallbackPrice,
  eligibility,
  locale,
  firstLessonId,
  sections,
}: Props) {
  const t = useTranslations("courseDetail");
  const { plans, selectedPlan, mode } = useCoursePricing();
  // `SITE-CRS01`: the bar repeats the first screen's purchase card, so it stays
  // away until that card has scrolled off (the hero's end when there is none).
  const visible = useScrolledPast([HERO_BUY_ID, "course-hero-scroll-sentinel"]);
  const { isAuthenticated, getToken } = useAuth();
  const hasEligibilityGate = !!eligibility;
  const { data: eligibilityStatus } = useCourseEligibilityStatus(
    { slug, eligibility },
    isAuthenticated && hasEligibilityGate,
  );
  const eligibilityPassed =
    hasEligibilityGate && isAuthenticated && eligibilityStatus?.passed === true;
  const eligibilityLocked = hasEligibilityGate && !eligibilityPassed;
  const eligibilityQuizSlug =
    eligibilityStatus?.quiz?.slug ?? eligibility?.quizSlug ?? "";
  const eligibilityAttemptHref = `/${locale}/quiz-exams/${eligibilityQuizSlug}/attempt`;
  const eligibilityCtaHref = isAuthenticated
    ? eligibilityAttemptHref
    : signInHref(locale, eligibilityAttemptHref);
  const token = getToken();
  const { data: library } = useLibrary(token ?? undefined);
  const ownedCourse = library?.courses.find((c) => c.courseId === courseId);
  const isOwned = !!ownedCourse;
  const pricingUiModel = useCoursePlanPricingUiModel(
    ownedCourse,
    isAuthenticated,
    plans,
  );

  const planName =
    getLocalizedField(
      selectedPlan?.name_en,
      selectedPlan?.name_bn,
      locale,
    ) ??
    selectedPlan?.name_en ??
    "";
  const copy = getOwnershipCopy(locale);

  const selectedIsUpgradeTarget =
    !!selectedPlan && isPlanIdActiveUpgradeTarget(selectedPlan._id, pricingUiModel);

  const selectedPlanOwnedState =
    selectedPlan && isAuthenticated && isOwned
      ? resolvePricingUiOwnedState(selectedPlan, pricingUiModel, {
          isAuthenticated,
          isOwned,
        })
      : null;

  const nominalLegacyPlan = pricingUiModel.nominalLegacyPlan;
  const nominalLegacyName =
    nominalLegacyPlan &&
    (getLocalizedField(
      nominalLegacyPlan.name_en,
      nominalLegacyPlan.name_bn,
      locale,
    ) ??
      nominalLegacyPlan.name_en);

  const ownedTierChipName =
    pricingUiModel.effectiveOwnedPlanForCopy &&
    (getLocalizedField(
      pricingUiModel.effectiveOwnedPlanForCopy.name_en,
      pricingUiModel.effectiveOwnedPlanForCopy.name_bn,
      locale,
    ) ??
      pricingUiModel.effectiveOwnedPlanForCopy.name_en);

  const mobilePeerNotice =
    selectedPlan && selectedPlanOwnedState
      ? selectedPlanOwnedState.kind === "owned-other" && !selectedPlanOwnedState.upgradeable
        ? t("planOwnership.coveredByPlan", {
            planName: ownedTierChipName ?? t("planOwnership.yourPlanShort"),
          })
        : selectedPlanOwnedState.kind === "owned-legacy-peer" && nominalLegacyName
          ? t("planOwnership.enrolledInPlanChip", { planName: nominalLegacyName })
          : undefined
      : undefined;

  const upgradePathBlocked =
    !!isAuthenticated &&
    !!isOwned &&
    !!selectedPlan &&
    resolvePlanPricingUiKind(selectedPlan, pricingUiModel, {
      isAuthenticated,
      isOwned,
    }) === "LOWER_OR_SIDEWAYS";
  const upgradeUnavailableReason = mobilePeerNotice
    ? undefined
    : upgradePathBlocked
      ? "This tier is not offered as an upgrade from your current access. You can continue learning on your included plan or pick an upgrade tier listed for your level."
      : undefined;
  const upgradeUnavailableLabel = mobilePeerNotice
    ? undefined
    : upgradePathBlocked && planName
      ? `${copy.upgradePrefix} ${planName}`
      : undefined;

  // ── Legacy or single: compact price + existing CTA (same as before) ────────
  if (mode === "loading" || mode === "legacy") {
    return (
      <MobileStickyShell sections={sections} visible={visible}>
        {isAuthenticated && isOwned && !pricingUiModel.hasActiveUpgradeTarget ? (
          <CompactAccessLabel label={getOwnershipCopy(locale).legacyBannerTitle} />
        ) : (
          <CompactPrice price={fallbackPrice} label={null} />
        )}
        {eligibilityLocked ? (
          <Button className="h-11 w-auto min-w-[44px] flex-shrink-0 px-6" asChild>
            <Link href={eligibilityCtaHref}>{t("eligibilityCtaPrimary")}</Link>
          </Button>
        ) : (
          <CourseEnrollCta
            courseId={courseId}
            slug={slug}
            price={fallbackPrice}
            locale={locale}
            firstLessonId={firstLessonId}
            size="sm"
            className="h-11 w-auto min-w-[44px] flex-shrink-0 px-6"
          />
        )}
      </MobileStickyShell>
    );
  }

  if (mode === "single" && selectedPlan) {
    return (
      <MobileStickyShell sections={sections} visible={visible}>
        {isAuthenticated && isOwned && !pricingUiModel.hasActiveUpgradeTarget ? (
          <CompactAccessLabel label={getOwnershipCopy(locale).legacyBannerTitle} />
        ) : (
          <CompactPrice price={selectedPlan.price} label={null} />
        )}
        {eligibilityLocked ? (
          <Button className="h-11 w-auto min-w-[44px] flex-shrink-0 px-6" asChild>
            <Link href={eligibilityCtaHref}>{t("eligibilityCtaPrimary")}</Link>
          </Button>
        ) : (
          <CourseEnrollCta
            courseId={courseId}
            slug={slug}
            price={selectedPlan.price}
            pricingPlanId={selectedPlan._id}
            locale={locale}
            firstLessonId={firstLessonId}
            size="sm"
            className="h-11 w-auto min-w-[44px] flex-shrink-0 px-6"
          />
        )}
      </MobileStickyShell>
    );
  }

  // ── Multi-tier: explicit selection drives price label + enroll CTA ─────────
  const activePlanPrice = selectedPlan?.price ?? fallbackPrice;
  const enrollLabel = activePlanPrice.isFree ? undefined : t("enrollNow");
  const upgradeLabel =
    selectedIsUpgradeTarget && selectedPlan
      ? formatTierUpgradeCtaLabel(selectedPlan, pricingUiModel, {
          upgradePay: (amount) => t("planOwnership.upgradePay", { amount }),
          upgradeFree: () => t("planOwnership.upgradeFree"),
        })
      : undefined;
  const upgradeToPlanId = selectedIsUpgradeTarget ? selectedPlan?._id : undefined;

  return (
    <MobileStickyShell sections={sections} visible={visible}>
      {isAuthenticated && isOwned && !pricingUiModel.hasActiveUpgradeTarget ? (
        <CompactAccessLabel label={copy.legacyBannerTitle} />
      ) : (
        <CompactPrice price={activePlanPrice} label={planName || null} />
      )}
      {eligibilityLocked ? (
        <Button className="h-11 w-auto min-w-[44px] flex-shrink-0 px-6" asChild>
          <Link href={eligibilityCtaHref}>{t("eligibilityCtaPrimary")}</Link>
        </Button>
      ) : (
        <CourseEnrollCta
          courseId={courseId}
          slug={slug}
          price={activePlanPrice}
          pricingPlanId={selectedPlan?._id}
          enrollLabel={enrollLabel}
          upgradeToPlanId={upgradeToPlanId}
          upgradeLabel={upgradeLabel}
          upgradeUnavailableReason={upgradeUnavailableReason}
          upgradeUnavailableLabel={upgradeUnavailableLabel}
          enrolledPeerNotice={mobilePeerNotice}
          locale={locale}
          firstLessonId={firstLessonId}
          size="sm"
          className="h-11 w-auto min-w-[44px] flex-shrink-0 px-6"
        />
      )}
    </MobileStickyShell>
  );
}

function MobileStickyShell({
  children,
  sections,
  visible,
}: {
  children: React.ReactNode;
  sections: CoursePageSection[];
  visible: boolean;
}) {
  return (
    <div
      aria-hidden={!visible}
      inert={!visible}
      data-bottom-bar="phone"
      className={cn(
        "fixed bottom-0 left-0 right-0 z-40 border-t border-white/10 bg-neutral-950 px-4 text-white shadow-[0_-8px_30px_rgba(0,0,0,0.14)] transition-transform duration-200 ease-out motion-reduce:transition-none lg:hidden",
        visible ? "translate-y-0" : "pointer-events-none translate-y-full",
      )}
      style={{
        paddingBottom: "max(10px, env(safe-area-inset-bottom, 0px))",
      }}
    >
      <div className="flex min-h-11 items-center justify-between gap-3 pt-2.5">
        <CourseMobileSectionSheet sections={sections} tone="dark" />
        {children}
      </div>
    </div>
  );
}

function CompactPrice({
  price,
  label,
}: {
  price: CoursePrice;
  label: string | null;
}) {
  return (
    <div className={cn("min-w-0 flex-1", label && "flex flex-col gap-0.5")}>
      {label && (
        <p className="truncate text-[11px] font-medium text-neutral-400">
          {label}
        </p>
      )}
      <p
        className={cn(
          "font-bold",
          price.isFree ? "text-emerald-300" : "text-white",
          label ? "text-[16px]" : "text-[20px]",
        )}
        aria-live="polite"
      >
        {formatCompactPrice(price)}
      </p>
    </div>
  );
}

function CompactAccessLabel({ label }: { label: string }) {
  return (
    <div className="min-w-0 flex-1">
      <p className="truncate text-[13px] font-semibold text-emerald-300">
        {label}
      </p>
    </div>
  );
}
